import React, { useEffect, useContext, useState, ReactNode } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useSignUpFlow, LookupResult } from "#/components/context/signup/SignUpFlowContext"
import { useHistory } from "react-router"
import { isHttpError } from "#/utils/HttpErrors"
import { CarrotErrorCodes, getErrorMessageFromCode } from "#/utils/CarrotErrors"
import { Link } from "@carrotfertility/carotene-core"
import { useIntl } from "react-intl"
import { EmployeeSupportUrl } from "../../../utils/EmployeeSupportLink"
import { carrotClient } from "#/utils/CarrotClient"

interface SignUpFindAccountSuccessProviderProps {
  onSubmit: ({ email }: { email: any }) => Promise<void>
  getTranslatedErrorMessage: (errorCode: string) => string | ReactNode | (string | ReactNode)[]
  error: string
  loading: boolean
}

const SignUpFindAccountSuccessContext = React.createContext<SignUpFindAccountSuccessProviderProps>(null)

export const useSignUpFindAccountSuccess = () => useContext(SignUpFindAccountSuccessContext)

// @ts-expect-error TS(7031) FIXME: Binding element 'children' implicitly has an 'any'... Remove this comment to see the full error message
export function SignUpFindAccountSuccessProvider({ children }) {
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const formMethods = useForm({ reValidateMode: "onSubmit" })
  const history = useHistory()
  const intl = useIntl()
  const { locale } = intl
  const { getHasFoundAccount, lookupAccountByEmail, firstName, lastName, dateOfBirth } = useSignUpFlow()

  useEffect(() => {
    if (!getHasFoundAccount()) {
      history.push("/signup")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [])

  // @ts-expect-error TS(7031) FIXME: Binding element 'email' implicitly has an 'any' ty... Remove this comment to see the full error message
  const onSubmit = async ({ email }) => {
    setError("")
    setLoading(true)

    const lookupResult = await lookupAccountByEmail(email)

    switch (lookupResult.result) {
      case LookupResult.Proceed:
        // Continue to add email
        break
      case LookupResult.Handled:
        return
      case LookupResult.Error:
        setError(lookupResult.errorCode)
        setLoading(false)
        return
      default:
        // Unknown/future outcome → fail safe: surface an error, never auto-add the email.
        setError(CarrotErrorCodes.UNEXPECTED_ERROR)
        setLoading(false)
        return
    }

    try {
      await carrotClient.signUpAddEmailToAccount(email, firstName, lastName, dateOfBirth.format("YYYY-MM-DD"))
    } catch (error) {
      if (isHttpError(error)) {
        const { code } = await error.response.json()
        if (code === CarrotErrorCodes.EMPLOYEE_ALREADY_HAS_EMAIL) {
          setError(code)
          setLoading(false)
          return
        }
      }
      setError(CarrotErrorCodes.TRY_AGAIN_LATER)
      setLoading(false)
      return
    }

    try {
      await carrotClient.signUp(email, locale)
      history.push(`/signup-success?email=${encodeURIComponent(email)}`)
    } catch (error) {
      setError(CarrotErrorCodes.TRY_AGAIN_LATER)
      setLoading(false)
      return
    }
  }

  // @ts-expect-error TS(7006) FIXME: Parameter 'errorCode' implicitly has an 'any' type... Remove this comment to see the full error message
  const getTranslatedErrorMessage = (errorCode) => {
    if (errorCode === "MEMBER_INACTIVE") {
      return intl.formatMessage(getErrorMessageFromCode(errorCode), {
        link: (string) => (
          <Link color="inherit" href={EmployeeSupportUrl} target={"_blank"}>
            {string}
          </Link>
        )
      })
    }
    if (errorCode === CarrotErrorCodes.EMPLOYEE_ALREADY_HAS_EMAIL) {
      return intl.formatMessage(getErrorMessageFromCode(errorCode), {
        link: (string) => (
          <Link color="inherit" href={EmployeeSupportUrl} target={"_blank"}>
            {string}
          </Link>
        )
      })
    }
    return intl.formatMessage(getErrorMessageFromCode(errorCode))
  }

  return (
    <FormProvider {...formMethods}>
      <SignUpFindAccountSuccessContext.Provider value={{ error, loading, onSubmit, getTranslatedErrorMessage }}>
        {children}
      </SignUpFindAccountSuccessContext.Provider>
    </FormProvider>
  )
}
